import { useState, useEffect } from 'react';
import { Loader, Group, Alert, Space, Modal, Text, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import CustomCopyButton from '../custom-copy-button/custom-copy-button.component';
import { computerNumberStyles } from '../../assets/constants';

const generateAPIKey = async (userId) => {
    const response = await fetch(`/.netlify/functions/generateAPIKey`, {
        method: "POST",
        body: JSON.stringify({ userId }),
    });
    return await response.json();
}

const formatAPIKey = (key) => {
    if (key.length <= 7) {
        // If the key is too short to format, return it as is
        return key;
    }

    const firstThree = key.substring(0, 3);
    const lastFour = key.substring(key.length - 4);
    return `${firstThree}...${lastFour}`;
}

const APIModal = ({ modalOpen, setModalOpen, currentUser, APIKeys, setAPIKeys }) => {
    const [isLoadingAPIKey, setIsLoadingAPIKey] = useState(true);
    const [generationError, setGenerationError] = useState(false);
    const [newAPIKey, setNewAPIKey] = useState(null);

    const theme = useMantineTheme();
    const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`);

    useEffect(() => {
        async function fetchAPIKey() {
            if (currentUser && modalOpen) {
                try {
                    const { newAPIKey, creationDateString,timestamp, publicKey } = await generateAPIKey(currentUser.uid);
                    setNewAPIKey(newAPIKey);
                    const formattedAPIKey = formatAPIKey(newAPIKey);
                    setAPIKeys([...APIKeys, { key: formattedAPIKey, creationDateString, timestamp, publicKey }]);
                } catch (error) {
                    console.log(error);
                    setGenerationError(true);
                } finally {
                    setIsLoadingAPIKey(false);
                }
            }
        }

        fetchAPIKey();
    }, [currentUser,modalOpen]);

    return (
        <Modal
            opened={modalOpen}
            fullScreen={isXs}
            onClose={() => {
                setModalOpen(false)
                setIsLoadingAPIKey(true)
                setGenerationError(false)
            }}
            title={isLoadingAPIKey ? `Generating API key...` : `Your API key`}
            radius='lg'
            size='md'
            centered
            styles={{
                title: {
                    fontSize: '18px',
                    fontWeight: 600,
                },
            }}
            transitionProps={{ transition: 'fade', duration: 200 }}
        >
            {
                generationError ?
                    <>
                        <Alert title="Oops!" color="red" radius='lg'>
                            There was a problem generating your API key.
                            Please try again or contact us at support@roundtable.ai
                        </Alert>
                        <Space h='xl' />
                    </>
                    :
                    isLoadingAPIKey ?
                        <Group position='center'> <Loader /> </Group>
                        :
                        <Group position='center'>
                            <Text size='lg' sx = {{...computerNumberStyles}}>
                                {newAPIKey}
                            </Text>
                            <CustomCopyButton link={newAPIKey} />
                        </Group>
            }
        </Modal>
    )
}

export default APIModal;